import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useErrorContext } from '@qpr/client-library';
import { SIPOCViewModelType } from '@qpr/sipoc/views/sipoc-view-model';
import React, { createContext, useEffect, useState } from 'react';
import { isValidId } from '../../../utils/url-utils';
import ProcessChain from '../components/ProcessChain';
import SipocGrid from '../components/SipocGrid';
import { initialLowerState, initialUpperState } from '../sipoc-state';

const PREFIX = 'SipocView';

const classes = {
  pageTitleFont: `${PREFIX}-pageTitleFont`
};

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.pageTitleFont}`]: {
    padding: theme.spacing(3),
    color: theme.palette.getContrastText(theme.palette.text.primary)
  }
}));

interface SIPOCViewProps {
  SIPOCViewModel: SIPOCViewModelType;
}
export const ActiveSipocId = createContext('');

const SipocView = ({ SIPOCViewModel }: SIPOCViewProps) => {
  const viewModel: SIPOCViewModelType = SIPOCViewModel;

  const [upperState, setUpperState] = useState(initialUpperState);
  const [lowerState, setLowerState] = useState(initialLowerState);
  const { setError } = useErrorContext();

  useEffect(() => {
    setLowerState(initialLowerState);
    setUpperState(initialUpperState);
    async function initData() {
      if (!isValidId(viewModel.businessProcessId)) {
        setError({
          message: 'Invalid parameter.',
          details: `element id malformed ${viewModel.businessProcessId}`
        });
        setUpperState({ ...initialUpperState, inited: true });
        setLowerState({ ...initialLowerState, inited: true });
        return;
      }

      const upperPart = await viewModel.getUpperData();
      setUpperState(upperPart);

      if (viewModel.sipocId !== '-' && !isValidId(viewModel.sipocId)) {
        setLowerState({ ...initialLowerState, inited: true });
        setError({
          message: 'Invalid parameter.',
          details: `element id malformed ${viewModel.sipocId}`
        });
        return;
      }

      setError(null);

      const lowerPart = await viewModel.getLowerData();
      if (upperPart.error.message || lowerPart.error.message) {
        setError({
          message: (
            <span>
              {upperPart.error.message}
              {upperPart.error.message && lowerPart.error.message && <br />}
              {lowerPart.error.message}
            </span>
          ),
          details: (
            <span>
              {upperPart.error.details}
              {upperPart.error.details && lowerPart.error.details && <br />}
              {lowerPart.error.details}
            </span>
          )
        });
      }
      setLowerState(lowerPart);
    }
    initData();
  }, [viewModel.businessProcessId, viewModel.sipocId]);

  return (
    <Root
      data-testid="sipoc-title"
      data-test-businessprocessid={viewModel.businessProcessId}
      data-test-sipocid={viewModel.sipocId}
    >
      <Typography variant="h6" className={classes.pageTitleFont}>
        Process overview
      </Typography>
      <ActiveSipocId.Provider value={viewModel.sipocId}>
        <ProcessChain cards={upperState.data} inited={upperState.inited} />
        <SipocGrid
          gridHeader={lowerState.header}
          elements={lowerState.data}
          inited={lowerState.inited}
        />
      </ActiveSipocId.Provider>
    </Root>
  );
};

export default SipocView;
